import React, { useState } from 'react';
import { Box, Paper, Typography, Stack, Chip, CircularProgress, Tooltip, useTheme, alpha } from '@mui/material';
import {
  FiberManualRecord,
  AccessTime as AccessTimeIcon,
  PersonOutline as PersonOutlineIcon,
  DownloadOutlined as DownloadIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  DescriptionOutlined as DescriptionIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useSelector } from 'react-redux';
import { RootStateType } from '@Store';

dayjs.extend(relativeTime);

interface UserReportCardProps {
  type?: string;
  url: string | null;
  createdAt?: string;
  status?: string;

  // ✅ NEW PROPS
  loading?: boolean;
  error?: string | null | boolean;
}

// Function to update the label based on the type
const updateLabel = (typeToMatch: string) => {
  const options = [
    { type: 'ACCOUNTS', label: 'Account Detailed' },
    { type: 'ACCOUNTS_SUMMARY', label: 'Accounts Summary' },
    { type: 'CS', label: 'Customer Service Detailed' },
    { type: 'OP', label: 'Operation Detailed' },
    { type: 'SALES', label: 'Sales Detailed' },
    { type: 'ALL', label: 'Shipment Report Detailed' },
    { type: 'CS_SUMMARY', label: 'Customer Service Summary' },
    { type: 'OP_SUMMARY', label: 'Operation Summary' },
    { type: 'SALES_SUMMARY', label: 'Sales Summary' },
    { type: 'ALL_SUMMARY', label: 'Shipment Report Summary' },
    { type: 'USER_SUMMARY', label: 'Shipment Report Summary' },
  ];

  const matchedOption = options.find(option => option.type === typeToMatch);
  return matchedOption ? 'Shipment Report' : typeToMatch;
};

const UserReportCard: React.FC<UserReportCardProps> = ({ type, url, createdAt, status, loading, error }) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);

  const label = type ? updateLabel(type) : undefined;
  const userName = useSelector((state: RootStateType) => state.loginUser.token?.name);
  const downloadStatus = useSelector((state: RootStateType) => state.basePricingRateSheetDownload);

  const handleDownload = () => {
    if (url && !loading && !error) {
      window.open(url, '_blank');
    }
  };

  // ✅ preserve old logic + add new props
  const isLoading = loading || downloadStatus?.downloadStatus === 'loading' || url === '';
  const isError = !!error || status === 'failed';
  const buttonText = isLoading ? 'In Progress' : 'Click here to download';

  return (
    <Paper
      elevation={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleDownload}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        p: 2,
        mt: 1.5,
        border: '1px solid',
        borderColor: isError
          ? alpha(theme.palette.error.main, 0.4)
          : isHovered && !isLoading
          ? alpha(theme.palette.primary.main, 0.4)
          : alpha(theme.palette.divider, 0.8),
        boxShadow:
          isHovered && !isLoading && !isError ? `0 6px 20px ${alpha(theme.palette.common.black, 0.06)}` : 'none',
        transform: isHovered && !isLoading ? 'translateY(-2px)' : 'none',
        transition: theme.transitions.create(['all'], {
          duration: theme.transitions.duration.shorter,
        }),
        cursor: isLoading ? 'not-allowed' : 'pointer',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          backgroundColor: isError
            ? theme.palette.error.main
            : isLoading
            ? theme.palette.warning.main
            : theme.palette.primary.main,
          opacity: isHovered ? 1 : 0.7,
          transition: theme.transitions.create(['width', 'opacity'], {
            duration: theme.transitions.duration.shorter,
          }),
        },
        '&:hover::before': {
          width: 6,
          opacity: 1,
        },
      }}
    >
      {/* Top Meta Bar */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1, pl: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, overflow: 'hidden' }}>
          {userName && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PersonOutlineIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  maxWidth: '120px',
                }}
              >
                {userName}
              </Typography>
            </Stack>
          )}

          {userName && createdAt && (
            <FiberManualRecord
              sx={{
                fontSize: 4,
                color: alpha(theme.palette.text.secondary, 0.4),
              }}
            />
          )}

          {createdAt && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                  fontWeight: 400,
                  fontSize: '0.75rem',
                }}
              >
                {dayjs(createdAt)?.fromNow()}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Status Badge */}
        {isError ? (
          <Chip
            size="small"
            icon={<ErrorOutlineIcon sx={{ fontSize: '13px !important' }} />}
            label="Failed"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              color: theme.palette.error.main,
              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        ) : isLoading ? (
          <Chip
            size="small"
            icon={<CircularProgress size={10} color="inherit" thickness={5} />}
            label="Processing"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: alpha(theme.palette.warning.main, 0.08),
              color: theme.palette.warning.dark || theme.palette.warning.main,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        ) : (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />}
            label="Ready"
            sx={{
              height: 22,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              color: theme.palette.success.main,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        )}
      </Stack>

      {/* Main Report Title */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ pl: 0.5, mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            backgroundColor: alpha(
              isError ? theme.palette.error.main : isLoading ? theme.palette.warning.main : theme.palette.primary.main,
              0.08,
            ),
            color: isError
              ? theme.palette.error.main
              : isLoading
              ? theme.palette.warning.main
              : theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          <DescriptionIcon sx={{ fontSize: 18 }} />
        </Box>

        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: '0.9rem',
            color: 'text.primary',
            lineHeight: 1.3,
            transition: theme.transitions.create('color', {
              duration: theme.transitions.duration.shorter,
            }),
            ...(isHovered &&
              !isLoading &&
              !isError && {
                color: theme.palette.primary.main,
              }),
          }}
        >
          {label || 'Report'}
        </Typography>
      </Stack>

      {/* Action / Status Prompt */}
      <Box
        sx={{
          pl: 0.5,
          pt: 1,
          borderTop: '1px solid',
          borderColor: alpha(theme.palette.divider, 0.5),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {isError ? (
            <>
              <ErrorOutlineIcon sx={{ fontSize: 15, color: theme.palette.error.main }} />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  fontSize: '0.78rem',
                }}
              >
                Failed to download, Please try again
              </Typography>
            </>
          ) : isLoading ? (
            <>
              <CircularProgress size={12} color="inherit" sx={{ color: theme.palette.text.disabled }} />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontWeight: 500,
                  fontSize: '0.78rem',
                }}
              >
                {buttonText}
              </Typography>
            </>
          ) : (
            <>
              <DownloadIcon
                sx={{
                  fontSize: 16,
                  color: theme.palette.primary.main,
                  transition: 'transform 0.2s',
                  transform: isHovered ? 'translateY(1px)' : 'none',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: '0.78rem',
                }}
              >
                {buttonText}
              </Typography>
            </>
          )}
        </Stack>

        {!isLoading && !isError && (
          <Tooltip title="Download file" arrow placement="top">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.primary.main,
                transition: 'transform 0.2s ease',
                transform: isHovered ? 'translateX(2px)' : 'none',
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 15 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};

export default UserReportCard;
